import { Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom';
import { useRef, type ReactNode } from 'react';
import { useAuth } from './auth';
import { RequireAuth } from './RequireAuth';
import { Navbar } from './components/Navbar';
import { BottomNav } from './components/BottomNav';
import { ScrollToTop } from './components/ScrollToTop';
import { SiteFooter } from './components/SiteFooter';
import { EarlyPhysio30Host } from './components/EarlyPhysio30Host';
import { AccountApprovedWelcomeHost } from './components/AccountApprovedWelcome';
import { PageLoader } from './components/ui';
import { navDirection, setNavDirection, type NavDirection } from './lib/motion';

// Public pages
import { HomePage, PlansPage, ClinicsPage, ClinicDetailPage, DoctorsPage, DoctorDetailPage, VerifyCertPage } from './pages/PublicPages';
import { EventsPage, EventDetailPage } from './pages/EventsPage';
import { BlogsPage, BlogDetailPage } from './pages/BlogPages';

// Auth pages
import { LoginPage } from './pages/LoginPage';
import { RegisterPage, RegisterSuccessPage } from './pages/RegisterPage';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/AuthPages';

// Student
import {
  StudentDashboardPage, CheckoutPage, PaymentReturnPage,
  NewBookingPage, NewConsultationPage, ConsultationDetailPage,
} from './pages/StudentDashboard';
import { StudentProfilePage } from './pages/StudentProfilePage';
import { InternshipCalendarPage } from './pages/InternshipCalendar';

// Doctor
import { DoctorDashboardPage } from './pages/DoctorDashboard';

// Clinic
import { ClinicOnboardingPage } from './pages/ClinicOnboardingPage';
import { ClinicOverviewPage } from './pages/ClinicDashboard';
import { ClinicSetupPage } from './pages/ClinicManagement';

// Admin
import {
  AdminOverviewPage, AdminStudentsPage, AdminClinicsPage, AdminDoctorsPage,
  AdminPlansPage, AdminPromoCodesPage, AdminPaymentsPage, AdminWithdrawalsPage, AdminReferralsPage,
  AdminSettingsPage, AdminBookingsPage, AdminConsultationsPage, AdminPermissionsPage, AdminEventsPage, AdminBlogsPage,
} from './pages/AdminDashboard';

function SmartRedirect() {
  const { me, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!me) return <Navigate to="/login" replace />;
  if (me.role === 'admin') return <Navigate to="/admin" replace />;
  if (me.role === 'clinic_staff') {
    if (me.needsClinicOnboarding || me.mustChangePassword) {
      return <Navigate to="/clinic/onboarding" replace />;
    }
    return <Navigate to="/clinic" replace />;
  }
  if (me.role === 'doctor') return <Navigate to="/doctor" replace />;
  return <Navigate to="/student" replace />;
}

/** Admins and clinic staff never see the public site — bounce them to their dashboard. */
function BlockDashboardOnly({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (me?.role === 'admin') return <Navigate to="/admin" replace />;
  if (me?.role === 'clinic_staff') {
    if (me.needsClinicOnboarding || me.mustChangePassword) {
      return <Navigate to="/clinic/onboarding" replace />;
    }
    return <Navigate to="/clinic" replace />;
  }
  return <>{children}</>;
}

/** Doctors can browse public content but not plans or student booking flows. */
function BlockDoctorPlansAndBooking({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (me?.role === 'doctor') return <Navigate to="/doctor" replace />;
  return <>{children}</>;
}

function resolveDir(from: string, to: string, navType: string): NavDirection {
  const dir = navDirection(from, to);
  if (navType !== 'POP') return dir;
  if (dir === 'forward') return 'back';
  if (dir === 'back') return 'forward';
  return 'fade';
}

/** Remounts on route change and plays a directional enter so page swaps feel continuous. */
function PageShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  const prevPath = useRef(pathname);
  const dirRef = useRef<NavDirection>('fade');

  if (prevPath.current !== pathname) {
    const resolved = resolveDir(prevPath.current, pathname, navType);
    dirRef.current = resolved;
    setNavDirection(resolved);
    prevPath.current = pathname;
  }

  return (
    <div
      key={pathname}
      className={`page-shell page-enter page-enter--${dirRef.current}`}
    >
      {children}
    </div>
  );
}

export default function App() {
  const { me } = useAuth();
  const isDashboardOnly = me?.role === 'admin' || me?.role === 'clinic_staff';

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <AccountApprovedWelcomeHost />
      <EarlyPhysio30Host />
      <PageShell>
        <Routes>
          {/* Public — blocked for admins & clinic staff */}
          <Route path="/" element={<BlockDashboardOnly><HomePage /></BlockDashboardOnly>} />
          <Route path="/plans" element={<BlockDashboardOnly><BlockDoctorPlansAndBooking><PlansPage /></BlockDoctorPlansAndBooking></BlockDashboardOnly>} />
          <Route path="/events" element={<BlockDashboardOnly><EventsPage /></BlockDashboardOnly>} />
          <Route path="/events/:idOrSlug" element={<BlockDashboardOnly><EventDetailPage /></BlockDashboardOnly>} />
          <Route path="/blogs" element={<BlockDashboardOnly><BlogsPage /></BlockDashboardOnly>} />
          <Route path="/blogs/:id" element={<BlockDashboardOnly><BlogDetailPage /></BlockDashboardOnly>} />
          <Route path="/clinics" element={<BlockDashboardOnly><ClinicsPage /></BlockDashboardOnly>} />
          <Route path="/clinics/:id" element={<BlockDashboardOnly><ClinicDetailPage /></BlockDashboardOnly>} />
          <Route path="/doctors" element={<BlockDashboardOnly><DoctorsPage /></BlockDashboardOnly>} />
          <Route path="/doctors/:id" element={<BlockDashboardOnly><DoctorDetailPage /></BlockDashboardOnly>} />
          <Route path="/verify/:certId" element={<BlockDashboardOnly><VerifyCertPage /></BlockDashboardOnly>} />

          {/* Auth */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/register-success" element={<RegisterSuccessPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<Navigate to="/login" replace />} />
          <Route path="/payment/return" element={<PaymentReturnPage />} />

          {/* Smart redirect after login */}
          <Route path="/dashboard" element={<SmartRedirect />} />

          {/* Student — tabbed single page; sub-pages stay as separate routes */}
          <Route path="/student" element={<RequireAuth role="student"><StudentDashboardPage /></RequireAuth>} />
          <Route path="/student/profile" element={<RequireAuth role="student"><StudentProfilePage /></RequireAuth>} />
          <Route path="/student/checkout/:planId" element={<RequireAuth role="student"><CheckoutPage /></RequireAuth>} />
          <Route path="/student/bookings/new" element={<RequireAuth role="student"><NewBookingPage /></RequireAuth>} />
          <Route path="/student/clinics/:clinicId/book" element={<RequireAuth role="student"><InternshipCalendarPage /></RequireAuth>} />
          <Route path="/student/consultations/new" element={<RequireAuth role="student"><NewConsultationPage /></RequireAuth>} />
          <Route path="/student/consultations/:id" element={<RequireAuth role="student"><ConsultationDetailPage /></RequireAuth>} />

          {/* Doctor — tabbed single page */}
          <Route path="/doctor" element={<RequireAuth role="doctor"><DoctorDashboardPage /></RequireAuth>} />

          {/* Clinic — single home page; management via top-right button */}
          <Route path="/clinic/onboarding" element={<RequireAuth role="clinic_staff"><ClinicOnboardingPage /></RequireAuth>} />
          <Route path="/clinic" element={<RequireAuth role="clinic_staff"><ClinicOverviewPage /></RequireAuth>} />
          <Route path="/clinic/management" element={<RequireAuth role="clinic_staff"><ClinicSetupPage /></RequireAuth>} />
          <Route path="/clinic/bookings" element={<RequireAuth role="clinic_staff"><Navigate to="/clinic" replace /></RequireAuth>} />
          <Route path="/clinic/setup" element={<RequireAuth role="clinic_staff"><Navigate to="/clinic/management" replace /></RequireAuth>} />
          <Route path="/clinic/profile" element={<RequireAuth role="clinic_staff"><Navigate to="/clinic/management" replace /></RequireAuth>} />
          <Route path="/clinic/portfolio" element={<RequireAuth role="clinic_staff"><Navigate to="/clinic/management" replace /></RequireAuth>} />
          <Route path="/clinic/schedules" element={<RequireAuth role="clinic_staff"><Navigate to="/clinic/management" replace /></RequireAuth>} />
          <Route path="/clinic/settings" element={<RequireAuth role="clinic_staff"><Navigate to="/clinic/management" replace /></RequireAuth>} />

          {/* Admin — overview open to any admin; other pages gated by module */}
          <Route path="/admin" element={<RequireAuth role="admin"><AdminOverviewPage /></RequireAuth>} />
          <Route path="/admin/students" element={<RequireAuth role="admin" adminModule="students"><AdminStudentsPage /></RequireAuth>} />
          <Route path="/admin/clinics" element={<RequireAuth role="admin" adminModule="clinics"><AdminClinicsPage /></RequireAuth>} />
          <Route path="/admin/clinics/new" element={<RequireAuth role="admin" adminModule="clinics"><AdminClinicsPage /></RequireAuth>} />
          <Route path="/admin/clinics/:id" element={<RequireAuth role="admin" adminModule="clinics"><AdminClinicsPage /></RequireAuth>} />
          <Route path="/admin/doctors" element={<RequireAuth role="admin" adminModule="doctors"><AdminDoctorsPage /></RequireAuth>} />
          <Route path="/admin/plans" element={<RequireAuth role="admin" adminModule="subscriptions"><AdminPlansPage /></RequireAuth>} />
          <Route path="/admin/events" element={<RequireAuth role="admin" adminModule="events"><AdminEventsPage /></RequireAuth>} />
          <Route path="/admin/blogs" element={<RequireAuth role="admin" adminModule="blogs"><AdminBlogsPage /></RequireAuth>} />
          <Route path="/admin/promo-codes" element={<RequireAuth role="admin" adminModule="promo_codes"><AdminPromoCodesPage /></RequireAuth>} />
          <Route path="/admin/payments" element={<RequireAuth role="admin" adminModule="payments"><AdminPaymentsPage /></RequireAuth>} />
          <Route path="/admin/withdrawals" element={<RequireAuth role="admin" adminModule="withdrawals"><AdminWithdrawalsPage /></RequireAuth>} />
          <Route path="/admin/referrals" element={<RequireAuth role="admin" adminModule="referrals"><AdminReferralsPage /></RequireAuth>} />
          <Route path="/admin/settings" element={<RequireAuth role="admin" adminModule="settings"><AdminSettingsPage /></RequireAuth>} />
          <Route path="/admin/bookings" element={<RequireAuth role="admin" adminModule="internships"><AdminBookingsPage /></RequireAuth>} />
          <Route path="/admin/consultations" element={<RequireAuth role="admin" adminModule="consultations"><AdminConsultationsPage /></RequireAuth>} />
          <Route path="/admin/permissions" element={<RequireAuth role="admin" adminModule="permissions"><AdminPermissionsPage /></RequireAuth>} />

          {/* 404 */}
          <Route path="*" element={
            <div className="flex flex-col items-center justify-center min-h-screen">
              <h1 className="font-heading text-4xl font-bold text-ink mb-2">404</h1>
              <p className="text-ink-soft mb-4">Page not found.</p>
              <a href={isDashboardOnly ? (me?.role === 'clinic_staff' ? '/clinic' : '/admin') : '/'} className="text-sand-strong hover:underline text-sm">
                {isDashboardOnly ? 'Go to dashboard' : 'Back to home'}
              </a>
            </div>
          } />
        </Routes>
      </PageShell>
      {!isDashboardOnly && <SiteFooter />}
      <BottomNav />
    </>
  );
}
